// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter } from '@stencil/core'
import {
  Component,
  Element,
  Event,
  h,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core'

import type { Attributes, QdsFocusEventDetail } from '../../helpers'
import {
  inheritAriaAttributes,
  invariant,
  pickFocusEventAttributes,
} from '../../helpers'
import type { Value } from '../controls'
import type { Size } from '../shared'

/**
 * `<qds-radio>` elements are rendered as circles that are filled when
 * selected. They are generally used in **radio groups** — collections of radio
 * buttons describing a set of related options.
 *
 * Only one radio button in a given radio group can be selected at the same
 * time.
 *
 * @see https://quartz.se.com/build/components/radio
 */
@Component({
  tag: 'qds-radio',
  scoped: true,
  styleUrl: 'radio.css',
})
export class Radio implements ComponentInterface {
  /**
   * Adds vertical margin to the radio for alignment.
   *
   * This is useful when creating inline layouts so that the first lines have
   * the correct vertical centering.
   */
  @Prop() public readonly inline: boolean = false

  /**
   * The radio button's size.
   */
  @Prop() public readonly size?: Size = 'standard'

  /**
   * The radio button's text.
   */
  @Prop() public readonly text?: string

  /**
   * Sets the radio button's state.
   *
   * @webnative
   */
  @Prop({ mutable: true }) public checked?: boolean

  /**
   * Prevents the radio button from being interacted with: it cannot be pressed
   * or focused.
   *
   * @webnative
   */
  @Prop() public readonly disabled?: boolean

  /**
   * The [`<form>`](https://developer.mozilla.org/docs/Web/HTML/Element/form)
   * element to associate the radio button with (its form owner).
   *
   * The value of this property must be the id of a `<form>` in the same
   * document. If this property is not set, the `<qds-radio>` is associated
   * with its ancestor `<form>` element, if any.
   *
   * This property lets you associate `<qds-radio>` elements to `<form>`s
   * anywhere in the document, not just inside a `<form>`. It can also override
   * an ancestor `<form>` element.
   *
   * @webnative
   */
  @Prop() public readonly form?: string

  /**
   * Specify the radio group this radio button belongs to.
   *
   * Once a radio group is established, selecting any radio button in that
   * group automatically deselects any currently-selected radio button in the
   * same group.
   *
   * @webnative
   */
  @Prop() public readonly name?: string

  /**
   * Specify the group the radio button belongs to as required.
   *
   * If any radio button in a same-named group of radio buttons has the
   * `required` attribute, a radio button in that group must be checked,
   * although it doesn't have to be the one with the attribute applied.
   *
   * @webnative
   */
  @Prop() public readonly required?: boolean

  /**
   * The value of the radio button, submitted as a name/value pair with form
   * data.
   *
   * @webnative
   */
  @Prop({ mutable: true }) public value?: Value

  /**
   * Emitted when the radio button loses focus.
   */
  @Event({ eventName: 'qdsBlur', bubbles: false, cancelable: false })
  private readonly blurEmitter!: EventEmitter<QdsFocusEventDetail>

  /**
   * Emitted when the radio button is checked by the user.
   */
  @Event({ eventName: 'qdsChange', cancelable: false })
  private readonly changeEmitter!: EventEmitter<void>

  /**
   * Emitted when the radio button gains focus.
   */
  @Event({ eventName: 'qdsFocus', bubbles: false, cancelable: false })
  private readonly focusEmitter!: EventEmitter<QdsFocusEventDetail>

  @Element() private readonly host!: HTMLElement

  @State() private tabIndex?: number

  #inheritedAttributes: Attributes = {}

  #input?: HTMLInputElement

  get #computedDisabled(): boolean {
    return this.disabled ?? false
  }

  get #computedSize(): Size {
    switch (this.size) {
      case 'standard':
      case 'small':
      case 'large': {
        return this.size
      }
      default: {
        return 'standard'
      }
    }
  }

  get #hasText(): boolean {
    return this.text !== undefined && this.text !== ''
  }

  @Listen('click')
  protected onClick(event: MouseEvent): void {
    if (this.#computedDisabled) {
      event.stopImmediatePropagation()
      return
    }

    invariant(this.#input)

    this.checked = this.#input.checked
  }

  @Watch('tabindex')
  protected tabindexChanged(newValue: string): void {
    const parsedValue = Number.parseInt(newValue, 10)
    this.tabIndex =
      parsedValue === 0 || Number.isNaN(parsedValue) ? undefined : parsedValue
  }

  public componentWillLoad(): void {
    this.#inheritedAttributes = inheritAriaAttributes(this.host)
    this.tabindexChanged(this.host.getAttribute('tabindex') ?? '0')
  }

  public render() {
    return (
      // eslint-disable-next-line jsx-a11y/label-has-associated-control
      <label
        aria-disabled={this.#computedDisabled ? 'true' : undefined}
        class={{
          'qds-inline': this.inline,
          'qds-label': true,
        }}
        data-size={this.#computedSize}
      >
        <div class="qds-container">
          <input
            checked={this.checked}
            class="qds-radio"
            data-size={this.#computedSize}
            disabled={this.disabled}
            form={this.form}
            name={this.name}
            onBlur={this.#onBlur}
            onChange={this.#onChange}
            onFocus={this.#onFocus}
            ref={this.#ref}
            required={this.required}
            tabIndex={this.#computedDisabled ? undefined : this.tabIndex}
            type="radio"
            value={this.value ?? undefined}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...this.#inheritedAttributes}
          />
          <div class="qds-box" data-size={this.#computedSize} />
          <qds-icon
            aria-hidden="true"
            class="qds-icon"
            data-size={this.#computedSize}
            library="core"
            name="checked"
          />
        </div>
        {this.#hasText && (
          <qds-label size={this.#computedSize} text={this.text} />
        )}
      </label>
    )
  }

  readonly #ref = (input?: HTMLInputElement): void => {
    this.#input = input
  }

  readonly #onBlur = (event: FocusEvent): void => {
    this.blurEmitter.emit(pickFocusEventAttributes(event))
  }

  readonly #onChange = (): void => {
    this.changeEmitter.emit()
  }

  readonly #onFocus = (event: FocusEvent): void => {
    this.focusEmitter.emit(pickFocusEventAttributes(event))
  }
}
